import { Minus, Plus } from 'lucide-react'
import { fmtMoneda } from '@/lib/cd2bPricing'
import { IconoItem } from '@/lib/cd2bIconos'

/**
 * Paquetes de CAPACIDAD (productos y DTE al mes) que el asesor puede vender sin cambiar de plan.
 *
 * A diferencia de los módulos —que se marcan o no— estos van por CANTIDAD: cada unidad sube el
 * tope del plan un `paso` (50 productos, 100 DTE). Por eso el control es un contador y no un
 * check, y por eso se muestra el tope RESULTANTE: al cliente no le dice nada "3 paquetes", le
 * dice "de 150 a 300 productos".
 *
 * Sobre un plan sin tope (Empresarial, Completa) no hay nada que ampliar: el paquete se muestra
 * deshabilitado en vez de dejar vender algo que no haría nada.
 *
 * Controlado por el padre: `capacidad` es `[{code, cantidad}]` y `setCapacidad` recibe el array
 * nuevo. `limites` son los topes vigentes del cliente (los del panel de impacto), para pintar
 * el "antes → después".
 */
export default function PaquetesCapacidad({ catalogo, capacidad = [], setCapacidad, limites, moneda = 'USD' }) {
  const paquetes = catalogo?.addons?.capacidad || []
  if (paquetes.length === 0) return null

  const cantidadDe = (code) => capacidad.find((c) => c.code === code)?.cantidad || 0

  const fijar = (code, n) => {
    const cantidad = Math.max(0, Math.floor(Number(n) || 0))
    const resto = capacidad.filter((c) => c.code !== code)
    setCapacidad(cantidad > 0 ? [...resto, { code, cantidad }] : resto)
  }

  return (
    <div className="space-y-2">
      {paquetes.map((p) => {
        const n = cantidadDe(p.code)
        // `limites` viene del backend con la clave del tope que amplía este paquete
        // (`productos` / `dteMes`); null = sin tope ⇒ el paquete no aplica.
        const topeBase = limites ? limites[p.limite] : undefined
        const ilimitado = topeBase === null
        const unidad = p.limite === 'dteMes' ? 'DTE/mes' : 'productos'
        return (
          <div key={p.code} className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-x-3 gap-y-1.5 py-1.5 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
            <IconoItem item={p} size={16} className="shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-body-sm text-neutral-800 dark:text-neutral-200 truncate">{p.label}</p>
              <p className="text-caption text-neutral-500 dark:text-neutral-400">
                {ilimitado
                  ? 'Este plan ya es ilimitado: no hay tope que ampliar.'
                  : <>+{p.paso} {unidad} por paquete · {fmtMoneda(p.precio, moneda)}/mes c/u</>}
              </p>
            </div>
            <div className="flex w-full sm:w-auto items-center justify-end gap-2 shrink-0">
              {/* `limites` ya viene del motor CON los paquetes sumados: es el cupo con el que
                  queda el cliente, no el de su edición. */}
              {!ilimitado && typeof topeBase === 'number' && (
                <span className={`text-caption tabular-nums whitespace-nowrap ${n > 0 ? 'text-success-700 dark:text-success-300 font-medium' : 'text-neutral-500 dark:text-neutral-400'}`}>
                  queda en {topeBase.toLocaleString('es-SV')} {unidad}
                </span>
              )}
              <div className="flex items-center gap-1">
                <button type="button" aria-label="Quitar un paquete" disabled={ilimitado || n === 0}
                  onClick={() => fijar(p.code, n - 1)}
                  className="inline-flex h-10 w-10 sm:h-8 sm:w-8 items-center justify-center rounded-md border border-line bg-card text-subtle disabled:opacity-40 hover:bg-surface-muted">
                  <Minus size={13} />
                </button>
                <input type="number" min="0" value={n} disabled={ilimitado}
                  onChange={(e) => fijar(p.code, e.target.value)}
                  className="h-10 sm:h-8 w-14 sm:w-12 rounded-md border border-line bg-card px-1 text-center text-body-sm text-foreground tabular-nums disabled:bg-surface-muted" />
                <button type="button" aria-label="Agregar un paquete" disabled={ilimitado}
                  onClick={() => fijar(p.code, n + 1)}
                  className="inline-flex h-10 w-10 sm:h-8 sm:w-8 items-center justify-center rounded-md border border-line bg-card text-subtle disabled:opacity-40 hover:bg-surface-muted">
                  <Plus size={13} />
                </button>
              </div>
            </div>
          </div>
        )
      })}
      <p className="text-caption text-neutral-500 dark:text-neutral-400">
        Suben el cupo sin cambiar de plan. Los recibos internos no consumen cuota de DTE.
      </p>
    </div>
  )
}
